import { ComponentType, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

const REASON_QUERY_PARAM = 'reason';
const REDACTED_VALUE = '[Filtered]';

/**
 * Removes the opt-out `reason` value (which can carry cardholder free-text) from
 * a URL's query string. Sentry captures outgoing request URLs on its default
 * HTTP breadcrumbs, and `sendDefaultPii` is enabled, so scrubbing here keeps the
 * HSS-4751 promise that raw free-text never reaches Sentry payloads.
 */
export function scrubReasonFromUrl(url: string): string {
  const queryStart = url.indexOf('?');
  if (queryStart === -1) return url;

  const params = new URLSearchParams(url.slice(queryStart + 1));
  if (!params.has(REASON_QUERY_PARAM)) return url;

  params.set(REASON_QUERY_PARAM, REDACTED_VALUE);
  return `${url.slice(0, queryStart)}?${params.toString()}`;
}

function scrubBreadcrumbUrl(breadcrumb: Sentry.Breadcrumb): void {
  if (breadcrumb.data && typeof breadcrumb.data.url === 'string') {
    breadcrumb.data.url = scrubReasonFromUrl(breadcrumb.data.url);
  }
}

/**
 * Breadcrumb data structure for error tracking
 */
export interface BreadcrumbData {
  category: string;
  message: string;
  level?: 'info' | 'warning' | 'error';
  data?: Record<string, any>;
}

/**
 * Error context for additional error information
 */
export interface ErrorContext {
  tags?: Record<string, string | number>;
  extra?: Record<string, any>;
}

/**
 * Error handler interface for abstracting error tracking implementations
 * This allows swapping out Sentry for any other error tracking service
 */
export interface ErrorHandler {
  /**
   * Initialize the error handler
   */
  init(config: ErrorHandlerConfig): void;

  /**
   * Capture an exception
   */
  captureException(error: any, context?: ErrorContext): void;

  /**
   * Capture a message
   */
  captureMessage(message: string, context?: ErrorContext & { level?: 'info' | 'warning' | 'error' }): void;

  /**
   * Add a breadcrumb for error tracking
   */
  addBreadcrumb(breadcrumb: BreadcrumbData): void;

  /**
   * Get the error boundary component
   */
  getErrorBoundary(): ComponentType<ErrorBoundaryProps>;
}

/**
 * Error handler configuration
 */
export interface ErrorHandlerConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  sendDefaultPii?: boolean;
}

/**
 * Error boundary props
 */
export interface ErrorBoundaryProps {
  fallback: ReactNode;
  showDialog?: boolean;
  children: ReactNode;
}

/**
 * Sentry-based error handler implementation
 * This is the concrete implementation using Sentry
 */
export class SentryErrorHandler implements ErrorHandler {
  init(config: ErrorHandlerConfig): void {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      sendDefaultPii: config.sendDefaultPii,
      beforeBreadcrumb(breadcrumb) {
        scrubBreadcrumbUrl(breadcrumb);
        return breadcrumb;
      },
      beforeSend(event) {
        if (typeof event.request?.url === 'string') {
          event.request.url = scrubReasonFromUrl(event.request.url);
        }
        if (typeof event.request?.query_string === 'string') {
          event.request.query_string = scrubReasonFromUrl(
            `?${event.request.query_string}`,
          ).slice(1);
        }
        event.breadcrumbs?.forEach(scrubBreadcrumbUrl);
        return event;
      },
    });
  }

  captureException(error: any, context?: ErrorContext): void {
    Sentry.captureException(error, {
      tags: context?.tags,
      extra: context?.extra,
    });
  }

  captureMessage(message: string, context?: ErrorContext & { level?: 'info' | 'warning' | 'error' }): void {
    Sentry.captureMessage(message, {
      level: context?.level || 'error',
      tags: context?.tags,
      extra: context?.extra,
    });
  }

  addBreadcrumb(breadcrumb: BreadcrumbData): void {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level || 'info',
      data: breadcrumb.data,
    });
  }

  getErrorBoundary(): ComponentType<ErrorBoundaryProps> {
    return Sentry.ErrorBoundary as ComponentType<ErrorBoundaryProps>;
  }
}

/**
 * No-op error handler for testing or when error tracking is disabled
 */
export class NoOpErrorHandler implements ErrorHandler {
  init(_config: ErrorHandlerConfig): void {
    // No-op
  }

  captureException(_error: any, _context?: ErrorContext): void {
    // No-op
  }

  captureMessage(_message: string, _context?: ErrorContext & { level?: 'info' | 'warning' | 'error' }): void {
    // No-op
  }

  addBreadcrumb(_breadcrumb: BreadcrumbData): void {
    // No-op
  }

  getErrorBoundary(): ComponentType<ErrorBoundaryProps> {
    // Return a simple pass-through component
    return ({ children }: ErrorBoundaryProps) => children as ReactNode;
  }
}

/**
 * Default error handler instance
 * Can be replaced with any ErrorHandler implementation
 * 
 * Example: Replace Sentry with a custom implementation
 * ```typescript
 * import { errorHandler, ErrorHandler } from './services/errorHandler';
 * 
 * class CustomErrorHandler implements ErrorHandler {
 *   init(config) 
 *   captureException(error, context) 
 *   captureMessage(message, context) 
 *   addBreadcrumb(breadcrumb)
 *   getErrorBoundary() 
 * }
 * 
 * // Replace the default handler
 * export const errorHandler = new CustomErrorHandler();
 * ```
 */
export const errorHandler: ErrorHandler = new SentryErrorHandler();

